import type { AnyRouter } from "@tanstack/react-router";
import { useAppVersionStore } from "./use-app-version-store";

/**
 * Apply a pending deploy by letting the browser load the user's next destination
 * as a new document, instead of the running (stale) bundle loading it.
 *
 * `onBeforeNavigate` is emitted before `matchRoutes`, so the hand-off happens
 * before the router imports a route chunk the new deployment may have replaced.
 * By then the history entry already points at the destination, so assigning the
 * same href replaces that entry rather than pushing a duplicate.
 */
export function subscribeToPendingUpdate(router: AnyRouter): () => void {
  return router.subscribe("onBeforeNavigate", (event) => {
    const { isUpdatePending, isWriting, actions } =
      useAppVersionStore.getState();

    if (!isUpdatePending) return;
    // Search-param and hash writes stay on this document: masked dialogs, list
    // filters, sheets and tabs write them constantly.
    if (!event.pathChanged) return;
    // A masked navigation's real href differs from the address bar; loading it
    // would strip the mask. Dialogs open in place; the update stays armed.
    if (event.toLocation.maskedLocation) return;
    // A document load aborts in-flight requests — defer to the next navigation.
    if (isWriting) return;
    // `assign` does not stop the in-flight SPA navigation, and more events can
    // arrive before the document is replaced.
    if (!actions.acquireRefresh()) return;

    window.location.assign(event.toLocation.href);
  });
}
